import { UseFormMethods } from "react-hook-form";
import { concat, EMPTY, Observable, of } from "rxjs";
import { AjaxError } from "rxjs/ajax";
import { catchError, exhaustMap, mergeMap } from "rxjs/operators";
import { PersonsApi } from "../api/persons-api";
import { QuotationPopupFormModelRaw } from "../containers/quotation-popup/types/quotation-popup";
import {
    catchErrorWithDispatch,
    handleApiError,
    isTokenExpiredError,
} from "../libs/utils/errors";
import { clearAccentMarks } from "../libs/utils/strings";
import {
    CustomerPatchNaturalPerson,
    CustomerSetRucHasLegalRepresentative,
} from "../redux/customer/actions/default";
import {
    LegalRepresentativePopupClosePopup,
    LegalRepresentativePopupSubmitForm,
    LegalRepresentativePopupSubmitFormDone,
    SaveLegalRepresentative,
} from "../redux/legal-representative-popup/actions/default";
import { PageOpenGenericErrorModal } from "../redux/page/actions/default";
import { QuotationPopupActions } from "../redux/quotation-popup/actions";
import { QuotationPopupValidateDocumentNumberDone } from "../redux/quotation-popup/actions/default";
import { SaveQuotation } from "../redux/quotation/actions/default";

export class PersonRepository {
    static validateDocumentNumber(
        action: QuotationPopupActions
    ): Observable<QuotationPopupActions> {
        const rhForm =
            action.payload as UseFormMethods<QuotationPopupFormModelRaw>;
        const { documentNumber, documentType } = rhForm.getValues();
        return PersonsApi.validateDocument(documentNumber, documentType).pipe(
            mergeMap((validateDocumentResponse) => {
                const hasLegalRepresentative =
                    validateDocumentResponse.message === "Existe Contratante";
                const hasValidDocument =
                    validateDocumentResponse.state === "ACTIVO";
                if (!hasValidDocument) {
                    rhForm.setError("documentNumber", {
                        type: "manual",
                        message: "RUC no válido",
                    });
                    return of(
                        new QuotationPopupValidateDocumentNumberDone(
                            false
                        ).create(),
                        new CustomerSetRucHasLegalRepresentative(false).create()
                    );
                }
                rhForm.clearErrors("documentNumber");
                return of(
                    new QuotationPopupValidateDocumentNumberDone(true).create(),
                    new CustomerSetRucHasLegalRepresentative(
                        hasLegalRepresentative
                    ).create(),
                    new CustomerPatchNaturalPerson({
                        name: validateDocumentResponse.businessName,
                        naturalName: validateDocumentResponse.name,
                        naturalLastName: validateDocumentResponse.lastName,
                        naturalMotherLastName:
                            validateDocumentResponse.motherLastName,
                    }).create()
                );
            }),
            catchError((error) => {
                if (isTokenExpiredError(error)) {
                    handleApiError(error);
                    return EMPTY;
                }
                rhForm.setError("documentNumber", {
                    type: "manual",
                    message: "RUC no válido",
                });
                const isDocumentNotFoundError =
                    error instanceof AjaxError &&
                    error.response?.equifaxResult?.code === "E004";
                const actions = [
                    new QuotationPopupValidateDocumentNumberDone(
                        false
                    ).create(),
                    new CustomerSetRucHasLegalRepresentative(false).create(),
                ];
                if (!isDocumentNotFoundError) {
                    actions.push(new PageOpenGenericErrorModal().create());
                }
                return of(...actions);
            })
        );
    }

    static saveLegalRepresentative(
        action: LegalRepresentativePopupSubmitForm
    ): Observable<LegalRepresentativePopupSubmitFormDone> {
        const legalRepresentative = {
            documentNumber: action.payload.documentNumber,
            name: clearAccentMarks(action.payload.firstName).trim(),
            firstSurname: clearAccentMarks(action.payload.firstSurname).trim(),
            lastSurname: clearAccentMarks(action.payload.lastSurname).trim(),
            position: clearAccentMarks(action.payload.position).trim(),
        };
        return PersonsApi.saveLegalRepresentative({
            documentId: action.payload.documentNumber,
            documentType: action.payload.documentType.toLowerCase(),
            employment: clearAccentMarks(action.payload.position).trim(),
            firstSurname: clearAccentMarks(action.payload.firstSurname).trim(),
            lastSurname: clearAccentMarks(action.payload.lastSurname).trim(),
            names: clearAccentMarks(action.payload.firstName).trim(),
        }).pipe(
            exhaustMap(() =>
                concat(
                    of(new LegalRepresentativePopupSubmitFormDone().create()),
                    of(new LegalRepresentativePopupClosePopup().create()),
                    of(new SaveQuotation().create()),
                    of(
                        new SaveLegalRepresentative(
                            legalRepresentative
                        ).create()
                    )
                )
            ),
            catchErrorWithDispatch((err) =>
                of(new LegalRepresentativePopupSubmitFormDone(err).create())
            )
        );
    }
}
